import type { User } from "firebase/auth";
import { apiFetchAdmin, apiFetchAdminMultipart } from "./fetch";
import type {
  PaginatedResponse,
  CursorPaginatedResponse,
  Store,
  StoreMember,
  MenuItem,
  MenuCategory,
  EventCategory,
  EventCategoryVisibilityScope,
  PointReward,
  StampCard,
  StampReward,
  UserStampCardEntry,
  StoreEvent,
  EventRegistration,
  EventRegistrationExport,
  EventInvitee,
  EventInviteStatus,
  GuestlistReportResponse,
  AdminUser,
  UserStore,
  DeviceToken,
  MyGuestlistCheckins,
  ActivityLog,
  StaffActivityLog,
  DeletionRequest,
  Report,
  DashboardSummary,
  PlatformMetrics,
  PlatformSettings,
  DefaultMenuCategory,
  PlacePrediction,
  PlaceDetails,
  AnalyticsPeriod,
  StoreAnalyticsSummary,
  FootTrafficPoint,
  StoreAnalyticsCustomer,
  LoyaltyWatchlistEntry,
  UserActivity,
  MerchantActivity,
  StoreReferralProgram,
  TargetDeal,
  TargetDealRequest,
  TargetedDealItem,
  BundleDeal,
  BundleDealRequest,
  BuyXGetYDeal,
  BuyXGetYDealRequest,
  AppointmentStaffSummary,
  AppointmentProfile,
  AppointmentService,
  Appointment,
  AppointmentPolicy,
  ConfigureAppointmentPolicyRequest,
  StoreAppointmentBan,
  FeaturedReward,
  FeaturedRewardType,
  RewardSearchResult,
  PermanentStaffInvitation,
} from "./types";
import { apiFetch } from "./fetch";

const A = "/api/v1/admin";

// ─── Dashboard ────────────────────────────────────────────────────────────────

export function getDashboardSummary(user: User) {
  return apiFetchAdmin<DashboardSummary>(`${A}/dashboard-summary`, user);
}

export function getPlatformMetrics(user: User) {
  return apiFetchAdmin<PlatformMetrics>(`${A}/metrics`, user);
}

export function createMerchantSSOToken(user: User) {
  return apiFetchAdmin<{ token: string }>(`${A}/auth/merchant-sso-token`, user, {
    method: "POST",
  });
}

// ─── Stores ───────────────────────────────────────────────────────────────────

export function listStores(
  user: User,
  params: {
    status?: string;
    name?: string;
    visible?: boolean;
    pending_approval?: boolean;
    limit?: number;
    offset?: number;
  }
) {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  if (params.name) q.set("name", params.name);
  if (params.visible != null) q.set("visible", String(params.visible));
  if (params.pending_approval != null) q.set("pending_approval", String(params.pending_approval));
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  return apiFetchAdmin<PaginatedResponse<Store>>(`${A}/stores?${q}`, user);
}

export function getStore(user: User, storeId: string) {
  return apiFetchAdmin<Store>(`${A}/stores/${storeId}`, user);
}

export function updateStore(user: User, storeId: string, data: Partial<Store>) {
  return apiFetchAdmin<Store>(`${A}/stores/${storeId}`, user, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export type AdminStoreUpdate = {
  name?: string;
  category?: string;
  status?: string;
  description?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  province?: string;
  country?: string;
  postal_code?: string;
  phone?: string;
  email?: string;
  public_phone?: string;
  public_email?: string;
  website?: string;
  schedule?: Record<string, { open: string; close: string }[]>;
};

export function adminUpdateStore(user: User, storeId: string, data: AdminStoreUpdate) {
  return apiFetchAdmin<Store>(`${A}/stores/${storeId}`, user, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteStore(user: User, storeId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}`, user, { method: "DELETE" });
}

export function suspendStore(user: User, storeId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}`, user, {
    method: "PUT",
    body: JSON.stringify({ status: "suspended" }),
  });
}

export async function getStoreMembers(user: User, storeId: string): Promise<StoreMember[]> {
  const res = await apiFetchAdmin<{ data: StoreMember[] }>(`${A}/stores/${storeId}/members`, user);
  return res.data;
}

export async function adminAddMember(
  user: User,
  storeId: string,
  userUid: string,
  role: "owner" | "manager" | "staff"
): Promise<void> {
  await apiFetchAdmin(`${A}/stores/${storeId}/members`, user, {
    method: "POST",
    body: JSON.stringify({ user_uid: userUid, role }),
  });
}

export async function adminRemoveMember(
  user: User,
  storeId: string,
  memberId: string
): Promise<void> {
  await apiFetchAdmin(`${A}/stores/${storeId}/members/${memberId}`, user, {
    method: "DELETE",
  });
}

// ─── Store approval ───────────────────────────────────────────────────────────

export function listPendingApprovalStores(user: User, params: { limit?: number; offset?: number }) {
  const q = new URLSearchParams();
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  return apiFetchAdmin<PaginatedResponse<Store>>(`${A}/stores/pending-approval?${q}`, user);
}

export function approveStore(user: User, storeId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/approve`, user, {
    method: "POST",
  });
}

export function unapproveStore(user: User, storeId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/unapprove`, user, {
    method: "POST",
  });
}

// ─── Store removal requests ───────────────────────────────────────────────────

export function listStoreRemovalRequests(user: User, params: { limit?: number; offset?: number }) {
  const q = new URLSearchParams();
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  return apiFetchAdmin<PaginatedResponse<Store>>(`${A}/stores/removal-requests?${q}`, user);
}

export function dismissStoreRemoval(user: User, storeId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/dismiss-removal`, user, {
    method: "POST",
  });
}

// ─── Price limit bypass ───────────────────────────────────────────────────────

export function listPriceLimitBypassRequests(
  user: User,
  params: { limit?: number; offset?: number }
) {
  const q = new URLSearchParams();
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  return apiFetchAdmin<PaginatedResponse<Store>>(
    `${A}/stores/price-limit-bypass-requests?${q}`,
    user
  );
}

export function approvePriceLimitBypass(user: User, storeId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/price-limit-bypass/approve`, user, {
    method: "POST",
  });
}

export function rejectPriceLimitBypass(user: User, storeId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/price-limit-bypass/reject`, user, {
    method: "POST",
  });
}

export function setPriceLimitBypass(user: User, storeId: string, bypass: boolean) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/price-limit-bypass`, user, {
    method: "POST",
    body: JSON.stringify({ bypass }),
  });
}

export function setEventsEnabled(user: User, storeId: string, enabled: boolean) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/events-enabled`, user, {
    method: "POST",
    body: JSON.stringify({ enabled }),
  });
}

// ─── Appointment booking opt-in ───────────────────────────────────────────────

export function listAppointmentsEnabledRequests(
  user: User,
  params: { limit?: number; offset?: number }
) {
  const q = new URLSearchParams();
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  return apiFetchAdmin<PaginatedResponse<Store>>(
    `${A}/stores/appointments-enabled-requests?${q}`,
    user
  );
}

export function approveAppointmentsEnabledRequest(user: User, storeId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/appointments-enabled/approve`, user, {
    method: "POST",
  });
}

export function rejectAppointmentsEnabledRequest(user: User, storeId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/appointments-enabled/reject`, user, {
    method: "POST",
  });
}

export function setAppointmentsEnabled(user: User, storeId: string, enabled: boolean) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/appointments-enabled`, user, {
    method: "POST",
    body: JSON.stringify({ enabled }),
  });
}

// ─── Store images ─────────────────────────────────────────────────────────────

export function uploadStoreImage(
  user: User,
  storeId: string,
  file: File,
  type:
    | "logo"
    | "cover"
    | "menu_item"
    | "point_reward"
    | "event_poster"
    | "stamp_reward"
    | "appointment_staff"
    | "event_category_banner"
) {
  const form = new FormData();
  form.append("file", file);
  form.append("type", type);
  return apiFetchAdminMultipart<{ url: string }>(
    `${A}/stores/${storeId}/images`,
    user,
    form,
    "POST"
  );
}

// ─── Store analytics ──────────────────────────────────────────────────────────

export function getStoreAnalyticsSummary(user: User, storeId: string, period: AnalyticsPeriod) {
  const q = new URLSearchParams({ period });
  return apiFetchAdmin<StoreAnalyticsSummary>(
    `${A}/stores/${storeId}/analytics/summary?${q}`,
    user
  );
}

export function getStoreFootTraffic(user: User, storeId: string, period: AnalyticsPeriod) {
  const q = new URLSearchParams({ period });
  return apiFetchAdmin<FootTrafficPoint[]>(
    `${A}/stores/${storeId}/analytics/foot-traffic?${q}`,
    user
  );
}

export function getStoreAnalyticsCustomers(
  user: User,
  storeId: string,
  params: { limit?: number; offset?: number } = {}
) {
  const q = new URLSearchParams();
  q.set("limit", String(params.limit ?? 100));
  q.set("offset", String(params.offset ?? 0));
  return apiFetchAdmin<PaginatedResponse<StoreAnalyticsCustomer>>(
    `${A}/stores/${storeId}/analytics/customers?${q}`,
    user
  );
}

export function getStoreLoyaltyWatchlist(user: User, storeId: string) {
  return apiFetchAdmin<LoyaltyWatchlistEntry[]>(
    `${A}/stores/${storeId}/analytics/loyalty-watchlist`,
    user
  );
}

// ─── User activity ────────────────────────────────────────────────────────────

export function getStoreUserActivity(
  user: User,
  storeId: string,
  params: { limit?: number; offset?: number } = {}
) {
  const q = new URLSearchParams();
  q.set("limit", String(params.limit ?? 20));
  q.set("offset", String(params.offset ?? 0));
  return apiFetchAdmin<PaginatedResponse<UserActivity>>(
    `${A}/stores/${storeId}/user-activities?${q}`,
    user
  );
}

export function getMerchantActivity(
  user: User,
  storeId: string,
  params: { limit?: number; offset?: number } = {}
) {
  const q = new URLSearchParams();
  q.set("limit", String(params.limit ?? 20));
  q.set("offset", String(params.offset ?? 0));
  return apiFetchAdmin<PaginatedResponse<MerchantActivity>>(
    `${A}/stores/${storeId}/merchant-activities?${q}`,
    user
  );
}

// ─── Menu items ───────────────────────────────────────────────────────────────

export async function listMenuItems(
  user: User,
  storeId: string,
  params: { search?: string; categoryId?: string; limit?: number; offset?: number } = {}
): Promise<PaginatedResponse<MenuItem>> {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.categoryId) qs.set("category_id", params.categoryId);
  qs.set("limit", String(params.limit ?? 20));
  qs.set("offset", String(params.offset ?? 0));
  return apiFetchAdmin<PaginatedResponse<MenuItem>>(
    `${A}/stores/${storeId}/menu-items?${qs}`,
    user
  );
}

function menuItemBody(data: Partial<MenuItem>) {
  return JSON.stringify({
    name: data.name,
    description: data.description,
    image_url: data.image_url,
    price_cents: data.price_cents,
    category_ids: data.category_ids,
    conditions: data.conditions,
    status: data.status,
    availability_schedule: data.availability_schedule,
  });
}

export function createMenuItem(user: User, storeId: string, data: Partial<MenuItem>) {
  return apiFetchAdmin<MenuItem>(`${A}/stores/${storeId}/menu-items`, user, {
    method: "POST",
    body: menuItemBody(data),
  });
}

export function updateMenuItem(
  user: User,
  storeId: string,
  itemId: string,
  data: Partial<MenuItem>
) {
  return apiFetchAdmin<MenuItem>(`${A}/stores/${storeId}/menu-items/${itemId}`, user, {
    method: "PUT",
    body: menuItemBody(data),
  });
}

export function deleteMenuItem(user: User, storeId: string, itemId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/menu-items/${itemId}`, user, {
    method: "DELETE",
  });
}

export type MenuItemDependentDeals = {
  bundle_deals: BundleDeal[];
  buy_x_get_y_deals: BuyXGetYDeal[];
};

export function getMenuItemDependentDeals(user: User, storeId: string, itemId: string) {
  return apiFetchAdmin<MenuItemDependentDeals>(
    `${A}/stores/${storeId}/menu-items/${itemId}/dependent-deals`,
    user
  );
}

export function reorderMenuItems(
  user: User,
  storeId: string,
  categoryId: string,
  orderedIds: string[]
) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/menu-items/reorder`, user, {
    method: "PUT",
    body: JSON.stringify({ category_id: categoryId, ordered_ids: orderedIds }),
  });
}

// Pages through the API's 100-item cap until the reported total is reached.
export async function listAllMenuItemsByCategory(
  user: User,
  storeId: string,
  categoryId: string
): Promise<MenuItem[]> {
  const items: MenuItem[] = [];
  let offset = 0;
  for (;;) {
    const page = await listMenuItems(user, storeId, { categoryId, limit: 100, offset });
    items.push(...page.data);
    offset += page.data.length;
    if (items.length >= page.total || page.data.length === 0) return items;
  }
}

// ─── Menu categories ──────────────────────────────────────────────────────────

export function listMenuCategories(user: User, storeId: string) {
  return apiFetchAdmin<MenuCategory[]>(`${A}/stores/${storeId}/menu-categories`, user);
}

export function createMenuCategory(user: User, storeId: string, name: string, description = "") {
  return apiFetchAdmin<MenuCategory>(`${A}/stores/${storeId}/menu-categories`, user, {
    method: "POST",
    body: JSON.stringify({ name, description }),
  });
}

export function updateMenuCategory(
  user: User,
  storeId: string,
  catId: string,
  data: { name: string; description: string; displayOrder: number }
) {
  return apiFetchAdmin<MenuCategory>(`${A}/stores/${storeId}/menu-categories/${catId}`, user, {
    method: "PUT",
    body: JSON.stringify({
      name: data.name,
      description: data.description,
      display_order: data.displayOrder,
    }),
  });
}

export function deleteMenuCategory(user: User, storeId: string, catId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/menu-categories/${catId}`, user, {
    method: "DELETE",
  });
}

// ─── Unlisted event categories ─────────────────────────────────────────────────

export function listEventCategories(user: User, storeId: string) {
  return apiFetchAdmin<EventCategory[]>(`${A}/stores/${storeId}/event-categories`, user);
}

export function createEventCategory(
  user: User,
  storeId: string,
  data: {
    name: string;
    url_param: string;
    banner_url?: string;
    visibility_scope: EventCategoryVisibilityScope;
  }
) {
  return apiFetchAdmin<EventCategory>(`${A}/stores/${storeId}/event-categories`, user, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateEventCategory(
  user: User,
  storeId: string,
  categoryId: string,
  data: {
    name: string;
    url_param: string;
    banner_url?: string;
    visibility_scope: EventCategoryVisibilityScope;
  }
) {
  return apiFetchAdmin<EventCategory>(
    `${A}/stores/${storeId}/event-categories/${categoryId}`,
    user,
    { method: "PUT", body: JSON.stringify(data) }
  );
}

export function deleteEventCategory(user: User, storeId: string, categoryId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/event-categories/${categoryId}`, user, {
    method: "DELETE",
  });
}

// ─── Target Deals ───────────────────────────────────────────────────────────

export async function listStoreTargetDeals(user: User, storeId: string): Promise<TargetDeal[]> {
  const res = await apiFetchAdmin<{ data: TargetDeal[] }>(
    `${A}/stores/${storeId}/target-deals`,
    user
  );
  return res.data;
}

function targetDealBody(data: TargetDealRequest) {
  return JSON.stringify({
    name: data.name,
    discount_type: data.discount_type,
    discount_percent: data.discount_percent,
    fixed_price_cents: data.fixed_price_cents,
    max_claims_per_customer_per_day: data.max_claims_per_customer_per_day,
    schedule: data.schedule,
    status: data.status,
    category_ids: data.category_ids,
    item_ids: data.item_ids,
    excluded_item_ids: data.excluded_item_ids ?? [],
    appointment_service_ids: data.appointment_service_ids ?? [],
  });
}

export function createStoreTargetDeal(user: User, storeId: string, data: TargetDealRequest) {
  return apiFetchAdmin<TargetDeal>(`${A}/stores/${storeId}/target-deals`, user, {
    method: "POST",
    body: targetDealBody(data),
  });
}

export function updateStoreTargetDeal(
  user: User,
  storeId: string,
  targetDealId: string,
  data: TargetDealRequest
) {
  return apiFetchAdmin<TargetDeal>(`${A}/stores/${storeId}/target-deals/${targetDealId}`, user, {
    method: "PUT",
    body: targetDealBody(data),
  });
}

export function deleteStoreTargetDeal(user: User, storeId: string, targetDealId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/target-deals/${targetDealId}`, user, {
    method: "DELETE",
  });
}

export async function listStoreTargetedDealItems(
  user: User,
  storeId: string,
  targetDealId: string
): Promise<TargetedDealItem[]> {
  const res = await apiFetchAdmin<{ data: TargetedDealItem[] }>(
    `${A}/stores/${storeId}/target-deals/${targetDealId}/targeted-items`,
    user
  );
  return res.data;
}

// ─── Bundle Deals ───────────────────────────────────────────────────────────

export async function listStoreBundleDeals(user: User, storeId: string): Promise<BundleDeal[]> {
  const res = await apiFetchAdmin<{ data: BundleDeal[] }>(
    `${A}/stores/${storeId}/bundle-deals`,
    user
  );
  return res.data;
}

function bundleDealBody(data: BundleDealRequest) {
  return JSON.stringify({
    name: data.name,
    bundle_price_cents: data.bundle_price_cents,
    max_claims_per_customer_per_day: data.max_claims_per_customer_per_day,
    schedule: data.schedule,
    status: data.status,
    items: data.items,
  });
}

export function createStoreBundleDeal(user: User, storeId: string, data: BundleDealRequest) {
  return apiFetchAdmin<BundleDeal>(`${A}/stores/${storeId}/bundle-deals`, user, {
    method: "POST",
    body: bundleDealBody(data),
  });
}

export function updateStoreBundleDeal(
  user: User,
  storeId: string,
  bundleDealId: string,
  data: BundleDealRequest
) {
  return apiFetchAdmin<BundleDeal>(`${A}/stores/${storeId}/bundle-deals/${bundleDealId}`, user, {
    method: "PUT",
    body: bundleDealBody(data),
  });
}

export function deleteStoreBundleDeal(user: User, storeId: string, bundleDealId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/bundle-deals/${bundleDealId}`, user, {
    method: "DELETE",
  });
}

// ─── Buy X Get Y Deals (Multi Buy) ──────────────────────────────────────────

export async function listStoreBuyXGetYDeals(user: User, storeId: string): Promise<BuyXGetYDeal[]> {
  const res = await apiFetchAdmin<{ data: BuyXGetYDeal[] }>(
    `${A}/stores/${storeId}/buy-x-get-y-deals`,
    user
  );
  return res.data;
}

function buyXGetYDealBody(data: BuyXGetYDealRequest) {
  return JSON.stringify({
    name: data.name,
    menu_item_id: data.menu_item_id,
    buy_quantity: data.buy_quantity,
    get_quantity: data.get_quantity,
    discount_type: data.discount_type,
    discount_value: data.discount_value,
    max_claims_per_customer_per_day: data.max_claims_per_customer_per_day,
    schedule: data.schedule,
    status: data.status,
  });
}

export function createStoreBuyXGetYDeal(user: User, storeId: string, data: BuyXGetYDealRequest) {
  return apiFetchAdmin<BuyXGetYDeal>(`${A}/stores/${storeId}/buy-x-get-y-deals`, user, {
    method: "POST",
    body: buyXGetYDealBody(data),
  });
}

export function updateStoreBuyXGetYDeal(
  user: User,
  storeId: string,
  buyXGetYDealId: string,
  data: BuyXGetYDealRequest
) {
  return apiFetchAdmin<BuyXGetYDeal>(
    `${A}/stores/${storeId}/buy-x-get-y-deals/${buyXGetYDealId}`,
    user,
    {
      method: "PUT",
      body: buyXGetYDealBody(data),
    }
  );
}

export function deleteStoreBuyXGetYDeal(user: User, storeId: string, buyXGetYDealId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/buy-x-get-y-deals/${buyXGetYDealId}`, user, {
    method: "DELETE",
  });
}

// ─── Appointment provider profile (a real member, not a separate "staff" row) ─

export function updateMemberAppointmentProfile(
  user: User,
  storeId: string,
  uid: string,
  data: Pick<StoreMember, "bio" | "photo_url" | "bookable" | "weekly_schedule">
) {
  return apiFetchAdmin<AppointmentProfile>(
    `${A}/stores/${storeId}/members/${uid}/appointment-profile`,
    user,
    {
      method: "PUT",
      body: JSON.stringify({
        bio: data.bio,
        photo_url: data.photo_url,
        bookable: data.bookable,
        weekly_schedule: data.weekly_schedule,
      }),
    }
  );
}

export function listStaffServices(user: User, storeId: string, uid: string) {
  return apiFetchAdmin<AppointmentService[]>(
    `${A}/stores/${storeId}/members/${uid}/services`,
    user
  );
}

export function configureStaffServices(
  user: User,
  storeId: string,
  uid: string,
  serviceIds: string[]
) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/members/${uid}/services`, user, {
    method: "PUT",
    body: JSON.stringify({ service_ids: serviceIds }),
  });
}

// ─── Appointment services ─────────────────────────────────────────────────────

export function listAppointmentServices(user: User, storeId: string) {
  return apiFetchAdmin<AppointmentService[]>(`${A}/stores/${storeId}/appointment-services`, user);
}

function appointmentServiceBody(data: Partial<AppointmentService>) {
  return JSON.stringify({
    name: data.name,
    description: data.description,
    duration_minutes: data.duration_minutes,
    price_cents: data.price_cents,
    weekly_schedule: data.weekly_schedule,
    capacity: data.capacity,
    active: data.active,
  });
}

export function createAppointmentService(
  user: User,
  storeId: string,
  data: Partial<AppointmentService>
) {
  return apiFetchAdmin<AppointmentService>(`${A}/stores/${storeId}/appointment-services`, user, {
    method: "POST",
    body: appointmentServiceBody(data),
  });
}

export function updateAppointmentService(
  user: User,
  storeId: string,
  serviceId: string,
  data: Partial<AppointmentService>
) {
  return apiFetchAdmin<AppointmentService>(
    `${A}/stores/${storeId}/appointment-services/${serviceId}`,
    user,
    { method: "PUT", body: appointmentServiceBody(data) }
  );
}

export function deleteAppointmentService(user: User, storeId: string, serviceId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/appointment-services/${serviceId}`, user, {
    method: "DELETE",
  });
}

export function listServiceStaff(user: User, storeId: string, serviceId: string) {
  return apiFetchAdmin<AppointmentStaffSummary[]>(
    `${A}/stores/${storeId}/appointment-services/${serviceId}/staff`,
    user
  );
}

// ─── Appointments (bookings) ──────────────────────────────────────────────────

export function listStoreAppointments(
  user: User,
  storeId: string,
  params: { limit?: number; offset?: number } = {}
) {
  const q = new URLSearchParams();
  q.set("limit", String(params.limit ?? 20));
  q.set("offset", String(params.offset ?? 0));
  return apiFetchAdmin<PaginatedResponse<Appointment>>(
    `${A}/stores/${storeId}/appointments?${q}`,
    user
  );
}

export function cancelAppointment(user: User, storeId: string, appointmentId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/appointments/${appointmentId}/cancel`, user, {
    method: "POST",
  });
}

export function markAppointmentNoShow(user: User, storeId: string, appointmentId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/appointments/${appointmentId}/no-show`, user, {
    method: "POST",
  });
}

export function completeAppointment(
  user: User,
  storeId: string,
  appointmentId: string,
  amountCents?: number
) {
  return apiFetchAdmin<{
    points_earned: number;
    stamps_earned: number;
    charged_price_cents: number;
    price_adjusted: boolean;
  }>(`${A}/stores/${storeId}/appointments/${appointmentId}/complete`, user, {
    method: "POST",
    body: amountCents != null ? JSON.stringify({ amount_cents: amountCents }) : undefined,
  });
}

// ─── Appointment cancellation policy & bans (any store) ───────────────────────

export function getStoreAppointmentPolicy(user: User, storeId: string) {
  return apiFetchAdmin<AppointmentPolicy>(`${A}/stores/${storeId}/appointment-policy`, user);
}

export function configureStoreAppointmentPolicy(
  user: User,
  storeId: string,
  data: ConfigureAppointmentPolicyRequest
) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/appointment-policy`, user, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function listStoreAppointmentBans(user: User, storeId: string) {
  return apiFetchAdmin<StoreAppointmentBan[]>(`${A}/stores/${storeId}/appointment-bans`, user);
}

export function imposeStoreAppointmentBan(
  user: User,
  storeId: string,
  appointmentId: string,
  reason?: string
) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/appointments/${appointmentId}/ban`, user, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? "" }),
  });
}

export function liftStoreAppointmentBan(user: User, storeId: string, banId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/appointment-bans/${banId}/lift`, user, {
    method: "POST",
  });
}

export function reorderMenuCategories(user: User, storeId: string, orderedIds: string[]) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/menu-categories/reorder`, user, {
    method: "PUT",
    body: JSON.stringify({ ordered_ids: orderedIds }),
  });
}

// ─── Point Rewards ────────────────────────────────────────────────────────────

export async function listPointRewards(user: User, storeId: string): Promise<PointReward[]> {
  const res = await apiFetchAdmin<PaginatedResponse<PointReward>>(
    `${A}/stores/${storeId}/point-rewards`,
    user
  );
  return res.data;
}

function pointRewardBody(data: Partial<PointReward>) {
  return JSON.stringify({
    name: data.name,
    description: data.description,
    image_url: data.image_url,
    price_cents: data.price_cents,
    points_required: data.points_required,
    conditions: data.conditions,
    status: data.status,
  });
}

export function createPointReward(user: User, storeId: string, data: Partial<PointReward>) {
  return apiFetchAdmin<PointReward>(`${A}/stores/${storeId}/point-rewards`, user, {
    method: "POST",
    body: pointRewardBody(data),
  });
}

export function updatePointReward(
  user: User,
  storeId: string,
  rewardId: string,
  data: Partial<PointReward>
) {
  return apiFetchAdmin<PointReward>(`${A}/stores/${storeId}/point-rewards/${rewardId}`, user, {
    method: "PUT",
    body: pointRewardBody(data),
  });
}

export function deletePointReward(user: User, storeId: string, rewardId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/point-rewards/${rewardId}`, user, {
    method: "DELETE",
  });
}

// ─── Stamp card ───────────────────────────────────────────────────────────────

export function getStampCard(user: User, storeId: string): Promise<StampCard | null> {
  return apiFetchAdmin<StampCard | null>(`${A}/stores/${storeId}/stamp-card`, user);
}

export function updateStampCard(
  user: User,
  storeId: string,
  data: { enabled: boolean; stampMinPriceCents: number; stampDailyLimit?: number }
) {
  return apiFetchAdmin<StampCard>(`${A}/stores/${storeId}/stamp-card`, user, {
    method: "PUT",
    body: JSON.stringify({
      enabled: data.enabled,
      // Integer cents; 0 clears the minimum (every order earns 1 stamp).
      stamp_min_price_cents: data.stampMinPriceCents,
      // 1–100; 0 resets to the platform default; omitted (undefined) keeps the current limit.
      stamp_daily_limit: data.stampDailyLimit,
    }),
  });
}

// ─── Store referral program ────────────────────────────────────────────────────

export function getReferralProgram(
  user: User,
  storeId: string
): Promise<StoreReferralProgram | null> {
  return apiFetchAdmin<StoreReferralProgram | null>(
    `${A}/stores/${storeId}/referral-program`,
    user
  );
}

export function updateReferralProgram(user: User, storeId: string, data: StoreReferralProgram) {
  return apiFetchAdmin<StoreReferralProgram>(`${A}/stores/${storeId}/referral-program`, user, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function listStampRewards(user: User, storeId: string): Promise<StampReward[]> {
  const res = await apiFetchAdmin<PaginatedResponse<StampReward>>(
    `${A}/stores/${storeId}/stamp-rewards`,
    user
  );
  return res.data;
}

function stampRewardBody(data: Partial<StampReward>) {
  return JSON.stringify({
    name: data.name,
    description: data.description,
    image_url: data.image_url,
    stamps_required: data.stamps_required,
    price_cents: data.price_cents,
    status: data.status,
  });
}

export function createStampReward(user: User, storeId: string, data: Partial<StampReward>) {
  return apiFetchAdmin<StampReward>(`${A}/stores/${storeId}/stamp-rewards`, user, {
    method: "POST",
    body: stampRewardBody(data),
  });
}

export function updateStampReward(
  user: User,
  storeId: string,
  rewardId: string,
  data: Partial<StampReward>
) {
  return apiFetchAdmin<StampReward>(`${A}/stores/${storeId}/stamp-rewards/${rewardId}`, user, {
    method: "PUT",
    body: stampRewardBody(data),
  });
}

export function deleteStampReward(user: User, storeId: string, rewardId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/stamp-rewards/${rewardId}`, user, {
    method: "DELETE",
  });
}

// ─── Events ───────────────────────────────────────────────────────────────────

// Unused by EventsTab (now listUpcomingEvents/listPastEvents) — used by EventsExportPage's Event filter instead.
export function listEvents(
  user: User,
  storeId: string,
  limit: number,
  offset: number,
  order?: "asc"
): Promise<PaginatedResponse<StoreEvent>> {
  const q = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (order) q.set("order", order);
  return apiFetchAdmin(`${A}/stores/${storeId}/events?${q}`, user);
}

function listEventsByStatus(
  status: "upcoming" | "past",
  user: User,
  storeId: string,
  limit: number,
  cursor?: string
): Promise<CursorPaginatedResponse<StoreEvent>> {
  const q = new URLSearchParams({ status, limit: String(limit) });
  if (cursor) q.set("cursor", cursor);
  return apiFetchAdmin(`${A}/stores/${storeId}/events?${q}`, user);
}

export function listUpcomingEvents(user: User, storeId: string, limit: number, cursor?: string) {
  return listEventsByStatus("upcoming", user, storeId, limit, cursor);
}

export function listPastEvents(user: User, storeId: string, limit: number, cursor?: string) {
  return listEventsByStatus("past", user, storeId, limit, cursor);
}

function eventBody(data: Partial<StoreEvent>) {
  return JSON.stringify({
    title: data.title,
    subtitle: data.subtitle,
    description: data.description,
    poster_url: data.poster_url,
    points: data.points,
    price_cents: data.price_cents,
    discounted_price_cents: data.discounted_price_cents,
    guest_limit_per_event: data.guest_limit_per_event,
    guest_limit_per_signup: data.guest_limit_per_signup,
    conditions: data.conditions,
    starts_at: data.starts_at,
    ends_at: data.ends_at,
    visibility: data.visibility,
    category_id: data.category_id,
    venue_name: data.venue_name,
    venue_address_line1: data.venue_address_line1,
    venue_address_line2: data.venue_address_line2,
    venue_city: data.venue_city,
    venue_province: data.venue_province,
    venue_postal_code: data.venue_postal_code,
  });
}

export function createEvent(user: User, storeId: string, data: Partial<StoreEvent>) {
  return apiFetchAdmin<StoreEvent>(`${A}/stores/${storeId}/events`, user, {
    method: "POST",
    body: eventBody(data),
  });
}

export function updateEvent(
  user: User,
  storeId: string,
  eventId: string,
  data: Partial<StoreEvent>
) {
  return apiFetchAdmin<StoreEvent>(`${A}/stores/${storeId}/events/${eventId}`, user, {
    method: "PUT",
    body: eventBody(data),
  });
}

export function deleteEvent(user: User, storeId: string, eventId: string) {
  return apiFetchAdmin<void>(`${A}/stores/${storeId}/events/${eventId}`, user, {
    method: "DELETE",
  });
}

export function generateEventShareLink(user: User, storeId: string, eventId: string) {
  return apiFetchAdmin<{ invite_url: string }>(
    `${A}/stores/${storeId}/events/${eventId}/share-link`,
    user,
    { method: "POST" }
  );
}

export async function listEventRegistrations(
  user: User,
  storeId: string,
  eventId: string,
  params: { limit?: number; offset?: number } = {}
): Promise<{
  data: EventRegistration[];
  total: number;
  organic_count: number;
  invited_count: number;
  organic_checked_in_count: number;
  invited_checked_in_count: number;
}> {
  const q = new URLSearchParams();
  q.set("limit", String(params.limit ?? 20));
  q.set("offset", String(params.offset ?? 0));
  const res = await apiFetchAdmin<
    PaginatedResponse<EventRegistration> & {
      organic_count: number;
      invited_count: number;
      organic_checked_in_count: number;
      invited_checked_in_count: number;
    }
  >(`${A}/stores/${storeId}/events/${eventId}/registrations?${q}`, user);
  return {
    data: res.data,
    total: res.total,
    organic_count: res.organic_count,
    invited_count: res.invited_count,
    organic_checked_in_count: res.organic_checked_in_count,
    invited_checked_in_count: res.invited_checked_in_count,
  };
}

export async function listEventInvitees(
  user: User,
  storeId: string,
  eventId: string,
  params: { limit?: number; offset?: number } = {}
): Promise<{
  data: EventInvitee[];
  total: number;
  checked_in_count: number;
  additional_guest_count: number;
  additional_guest_checked_in_count: number;
}> {
  const q = new URLSearchParams();
  q.set("limit", String(params.limit ?? 20));
  q.set("offset", String(params.offset ?? 0));
  const res = await apiFetchAdmin<
    PaginatedResponse<EventInvitee> & {
      checked_in_count: number;
      additional_guest_count: number;
      additional_guest_checked_in_count: number;
    }
  >(`${A}/stores/${storeId}/events/${eventId}/invitees?${q}`, user);
  return {
    data: res.data,
    total: res.total,
    checked_in_count: res.checked_in_count,
    additional_guest_count: res.additional_guest_count,
    additional_guest_checked_in_count: res.additional_guest_checked_in_count,
  };
}

export async function listEventInviteStatus(
  user: User,
  storeId: string,
  eventId: string
): Promise<EventInviteStatus[]> {
  return apiFetchAdmin<EventInviteStatus[]>(
    `${A}/stores/${storeId}/events/${eventId}/invite-status`,
    user
  );
}

export async function getGuestlistReport(
  user: User,
  storeId: string,
  params: {
    startDate: string;
    endDate: string;
    isPromoter: boolean;
  }
): Promise<GuestlistReportResponse> {
  const q = new URLSearchParams({
    start_date: params.startDate,
    end_date: params.endDate,
    is_promoter: String(params.isPromoter),
  });
  return apiFetchAdmin<GuestlistReportResponse>(
    `${A}/stores/${storeId}/guestlist-report?${q}`,
    user
  );
}

export async function exportEventRegistrations(
  user: User,
  params: {
    store_id: string;
    event_id?: string;
    status?: "registered" | "checked_in";
    starts_after?: string;
    starts_before?: string;
  }
): Promise<EventRegistrationExport[]> {
  const q = new URLSearchParams({ store_id: params.store_id });
  if (params.event_id) q.set("event_id", params.event_id);
  if (params.status) q.set("status", params.status);
  if (params.starts_after) q.set("starts_after", params.starts_after);
  if (params.starts_before) q.set("starts_before", params.starts_before);
  return apiFetchAdmin<EventRegistrationExport[]>(`${A}/export/event-registrations?${q}`, user);
}

// ─── Users ────────────────────────────────────────────────────────────────────

export function listUsers(
  user: User,
  params: {
    search?: string;
    isPromoter?: boolean;
    orderBy?: string;
    orderDir?: "asc" | "desc";
    limit?: number;
    offset?: number;
  }
) {
  const q = new URLSearchParams();
  if (params.search) q.set("search", params.search);
  if (params.isPromoter != null) q.set("is_promoter", String(params.isPromoter));
  if (params.orderBy) q.set("order_by", params.orderBy);
  if (params.orderDir) q.set("order_dir", params.orderDir);
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  return apiFetchAdmin<PaginatedResponse<AdminUser>>(`${A}/users?${q}`, user);
}

export function getUser(user: User, uid: string) {
  return apiFetchAdmin<AdminUser>(`${A}/users/${uid}`, user);
}

export function updateUser(
  user: User,
  uid: string,
  data: {
    first_name?: string;
    last_name?: string;
    email_notifications_enabled?: boolean;
    is_promoter?: boolean;
  }
) {
  return apiFetchAdmin<AdminUser>(`${A}/users/${uid}`, user, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function getUserStores(user: User, uid: string): Promise<UserStore[]> {
  const res = await apiFetchAdmin<{ data: UserStore[] }>(`${A}/users/${uid}/stores`, user);
  return res.data;
}

export function getUserDeviceTokens(user: User, uid: string) {
  return apiFetchAdmin<DeviceToken[]>(`${A}/users/${uid}/device-tokens`, user);
}

export function getUserGuestlistCheckins(user: User, uid: string) {
  return apiFetchAdmin<MyGuestlistCheckins[]>(`${A}/users/${uid}/guestlist-checkins`, user);
}

export function sendUserNotification(
  user: User,
  uid: string,
  data: { title: string; body: string; imageUrl?: string }
) {
  return apiFetchAdmin<void>(`${A}/users/${uid}/notifications`, user, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function getUserActivity(
  user: User,
  uid: string,
  params: { limit?: number; offset?: number }
) {
  const q = new URLSearchParams();
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  return apiFetchAdmin<PaginatedResponse<ActivityLog>>(
    `${A}/users/${uid}/user-activities?${q}`,
    user
  );
}

export function getStaffActivity(
  user: User,
  staffID: string,
  params: { limit?: number; offset?: number }
) {
  const q = new URLSearchParams();
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  return apiFetchAdmin<PaginatedResponse<StaffActivityLog>>(
    `${A}/staff/${staffID}/user-activities?${q}`,
    user
  );
}

export function listDeletionRequests(user: User, params: { limit?: number; offset?: number }) {
  const q = new URLSearchParams();
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  return apiFetchAdmin<PaginatedResponse<DeletionRequest>>(
    `${A}/users/deletion-requests?${q}`,
    user
  );
}

export function deleteUser(user: User, uid: string) {
  return apiFetchAdmin<void>(`${A}/users/${uid}`, user, {
    method: "DELETE",
  });
}

export function suspendUser(user: User, uid: string, reason: string) {
  return apiFetchAdmin<void>(`${A}/users/${uid}/suspend`, user, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function reinstateUser(user: User, uid: string) {
  return apiFetchAdmin<void>(`${A}/users/${uid}/reinstate`, user, {
    method: "POST",
  });
}

export function flagUserName(user: User, uid: string, reason: string) {
  return apiFetchAdmin<void>(`${A}/users/${uid}/flag-name`, user, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function unflagUserName(user: User, uid: string) {
  return apiFetchAdmin<void>(`${A}/users/${uid}/unflag-name`, user, {
    method: "POST",
  });
}

export function adjustUserPointsBalance(user: User, uid: string, pointsBalance: number) {
  return apiFetchAdmin<void>(`${A}/users/${uid}/points-balance`, user, {
    method: "PUT",
    body: JSON.stringify({ points_balance: pointsBalance }),
  });
}

export async function getUserStampCards(user: User, uid: string): Promise<UserStampCardEntry[]> {
  const res = await apiFetchAdmin<{ data: UserStampCardEntry[] }>(
    `${A}/users/${uid}/stamp-cards`,
    user
  );
  return res.data;
}

export function adjustUserStampCard(user: User, uid: string, stampCardId: string, stamps: number) {
  return apiFetchAdmin<void>(`${A}/users/${uid}/stamp-cards/${stampCardId}`, user, {
    method: "PUT",
    body: JSON.stringify({ stamps }),
  });
}

// ─── Reports ──────────────────────────────────────────────────────────────────

export function listReports(
  user: User,
  params: {
    status?: string;
    targetType?: string;
    limit?: number;
    offset?: number;
  }
) {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  if (params.targetType) q.set("target_type", params.targetType);
  if (params.limit != null) q.set("limit", String(params.limit));
  if (params.offset != null) q.set("offset", String(params.offset));
  return apiFetchAdmin<PaginatedResponse<Report>>(`${A}/reports?${q}`, user);
}

export function getReport(user: User, reportId: string) {
  return apiFetchAdmin<Report>(`${A}/reports/${reportId}`, user);
}

export function updateReport(
  user: User,
  reportId: string,
  data: { status?: string; admin_notes?: string }
) {
  return apiFetchAdmin<Report>(`${A}/reports/${reportId}`, user, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ─── Settings ─────────────────────────────────────────────────────────────────

export function getSettings(user: User) {
  return apiFetchAdmin<PlatformSettings>(`${A}/settings`, user);
}

export function updateSettings(user: User, data: Partial<PlatformSettings>) {
  return apiFetchAdmin<PlatformSettings>(`${A}/settings`, user, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// ─── Default Menu Categories ─────────────────────────────────────────────────

export function getDefaultMenuCategories(user: User) {
  return apiFetchAdmin<DefaultMenuCategory[]>(`${A}/default-menu-categories`, user);
}

export function createDefaultMenuCategory(user: User, name: string) {
  return apiFetchAdmin<DefaultMenuCategory>(`${A}/default-menu-categories`, user, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

export function deleteDefaultMenuCategory(user: User, id: string) {
  return apiFetchAdmin<void>(`${A}/default-menu-categories/${id}`, user, { method: "DELETE" });
}

// ─── Featured Rewards ─────────────────────────────────────────────────────────

export function searchRewards(user: User, query: string, limit = 10) {
  const q = new URLSearchParams({ search: query, limit: String(limit) });
  return apiFetchAdmin<RewardSearchResult[]>(`${A}/rewards/search?${q}`, user);
}

export function listFeaturedRewards(user: User) {
  return apiFetchAdmin<FeaturedReward[]>(`${A}/featured-rewards`, user);
}

export function createFeaturedReward(user: User, rewardType: FeaturedRewardType, rewardId: string) {
  return apiFetchAdmin<FeaturedReward>(`${A}/featured-rewards`, user, {
    method: "POST",
    body: JSON.stringify({ reward_type: rewardType, reward_id: rewardId }),
  });
}

export function deleteFeaturedReward(user: User, id: string) {
  return apiFetchAdmin<void>(`${A}/featured-rewards/${id}`, user, { method: "DELETE" });
}

export function reorderFeaturedRewards(user: User, orderedIds: string[]) {
  return apiFetchAdmin<void>(`${A}/featured-rewards/reorder`, user, {
    method: "PUT",
    body: JSON.stringify({ ordered_ids: orderedIds }),
  });
}

// ─── Permanent Staff Invitations ─────────────────────────────────────────────

export function listPermanentStaffInvitations(user: User) {
  return apiFetchAdmin<{ invitations: PermanentStaffInvitation[] }>(
    `${A}/permanent-staff-invitations`,
    user
  ).then((r) => r.invitations);
}

export function createPermanentStaffInvitation(user: User, storeId: string) {
  return apiFetchAdmin<PermanentStaffInvitation>(
    `${A}/stores/${storeId}/permanent-staff-invitation`,
    user,
    { method: "POST" }
  );
}

export function deletePermanentStaffInvitation(user: User, id: string) {
  return apiFetchAdmin<void>(`${A}/permanent-staff-invitations/${id}`, user, { method: "DELETE" });
}

// ─── Places (public, no auth) ─────────────────────────────────────────────────

export async function searchPlaces(input: string, sessionToken: string) {
  const q = new URLSearchParams({ input, session_token: sessionToken });
  const data = await apiFetch<{ predictions: PlacePrediction[] }>(
    `/api/v1/public/places/autocomplete?${q}`
  );
  return data.predictions;
}

export function getPlaceDetails(placeId: string, sessionToken: string, mainText?: string) {
  const q = new URLSearchParams({ place_id: placeId, session_token: sessionToken });
  if (mainText) q.set("main_text", mainText);
  return apiFetch<PlaceDetails>(`/api/v1/public/places/details?${q}`);
}
